import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface SessionWarningProps {
  open: boolean;
  onClose: () => void;
  onExtend: () => void;
  timeRemaining: number; // in milliseconds
}

export function SessionWarning({ open, onClose, onExtend, timeRemaining }: SessionWarningProps) {
  const { signOut } = useAuth();
  const [countdown, setCountdown] = useState(Math.floor(timeRemaining / 1000));

  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open, timeRemaining]);

  useEffect(() => {
    setCountdown(Math.floor(timeRemaining / 1000));
  }, [timeRemaining]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    await signOut();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Aviso de Inatividade
          </DialogTitle>
          <DialogDescription className="pt-3">
            Sua sessão expirará em breve devido à inatividade. 
            Você deseja continuar trabalhando?
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex items-center justify-center py-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Tempo restante:</p>
            <p className="text-3xl font-bold text-primary tabular-nums">
              {formatTime(countdown)}
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleLogout}
            className="flex-1 sm:flex-none"
          >
            Sair agora
          </Button>
          <Button
            onClick={() => {
              onExtend();
              onClose();
            }}
            className="flex-1 sm:flex-none"
          >
            Continuar trabalhando
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}